import { Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import {
  FooterAction,
  LoadedUser,
  Section,
  SectionRow,
} from '../../../interfaces';
import {
  CampaignActionHandlers,
  MarketingCampaignWithSource,
  CampaignMetricData,
} from '../../../types/marketing';
import SideBarModal from '../../modal/SideBarModal';
import {
  campaignHasEmailStatistics,
  canEditCampaign,
  getCampaignActions,
  getCampaignLastEditedText,
  getCampaignMediumsText,
} from '../../../helpers/marketing';
import CampaignBadge from '../CampaignBadge';
import { formatDollarAmount, pluralize } from '../../../utils/global/helper';
import CampaignPreviewImage from './CampaignPreviewImage';
import useUser from '../../../hooks/global/useUser';
import { getFullName } from '../../../helpers/global';
import useGetCampaignMetrics from '../../../hooks/marketing/useGetCampaignMetrics';

interface Props {
  isOpen: boolean;
  campaign: MarketingCampaignWithSource | null;
  onClose: () => void;
  // Owner of the editor modal supplies action handlers + a disable flag.
  // Sidebar stays presentational — no mutations, no editor mount.
  actionHandlers?: CampaignActionHandlers;
  disableActions?: boolean;
}

function CampaignSideBarModal({
  isOpen,
  onClose,
  campaign,
  actionHandlers,
  disableActions,
}: Props) {
  const { user } = useUser<LoadedUser>();
  const { data: campaignMetricsData } = useGetCampaignMetrics(
    isOpen ? campaign?.id ?? null : null,
  );
  const campaignMetrics = campaignMetricsData?.metrics;
  const campaignCost = campaignMetricsData?.campaignCost ?? 0;

  if (!campaign) return null;

  // Strip onSelectCampaign + onEditCampaign — the sidebar IS the view (View
  // campaign / View All Metrics are redundant), and Edit lives in the footer.
  const moreActions = actionHandlers
    ? getCampaignActions(campaign, user, {
        ...actionHandlers,
        onSelectCampaign: undefined,
        onEditCampaign: undefined,
      })
    : undefined;

  const onEdit = actionHandlers?.onEditCampaign;
  const footerActions: FooterAction[] | undefined =
    onEdit && canEditCampaign(campaign, user)
      ? [{ label: 'Edit', action: () => onEdit(campaign) }]
      : undefined;

  const {
    source: { statistics },
  } = campaign;

  const hasEmailStatistics = campaignHasEmailStatistics(campaign);
  const editedText = getCampaignLastEditedText(campaign, true);
  const customersCount =
    campaign.source.statistics.total_customers_enrolled || 0;
  const sentCount =
    (hasEmailStatistics
      ? statistics?.emails?.sent
      : statistics.total_customers_finished) || 0;
  const employee =
    campaign.level === 'PRIVATE' && campaign.employeeId
      ? user?.business?.employees?.find(
          e => e.employeeId === campaign.employeeId,
        )
      : null;
  const employeeName =
    employee?.displayName ||
    getFullName({
      firstName: employee?.user?.firstName,
      lastName: employee?.user?.lastName,
    });
  const isDraft = campaign.source.status === 'draft';

  const metrics = [
    {
      label: 'Audience Size',
      type: 'TEXT',
      value: [
        `${customersCount || 0} ${pluralize('customer', customersCount)}`,
      ],
    },
    {
      label: 'Total Sent',
      type: 'TEXT',
      value: [`${sentCount || 0} `],
    },
    ...(hasEmailStatistics
      ? ([
          {
            label: 'Total Delivered',
            type: 'TEXT',
            value: [`${statistics.emails?.delivered || 0} `],
          },
          {
            label: 'Number of Opens',
            type: 'TEXT',
            value: [`${statistics.emails?.opened || 0} `],
          },
          {
            label: 'Hard Bounces',
            type: 'TEXT',
            value: [`${statistics.emails?.hard_bounced || 0} `],
          },
          {
            label: 'Soft Bounces',
            type: 'TEXT',
            value: [`${statistics.emails?.soft_bounced || 0} `],
          },
          statistics.emails?.clicked !== null && {
            label: 'Clicked',
            type: 'TEXT',
            value: [`${statistics.emails?.clicked || 0} `],
          },
        ].filter(Boolean) as SectionRow[])
      : []),
  ].filter(Boolean) as SectionRow[];

  const makeMetricRows = (
    metric: CampaignMetricData,
    includeConversion?: boolean,
  ): SectionRow[] =>
    [
      {
        label: 'Clicks',
        type: 'TEXT' as const,
        value: [`${metric.totalClicks}`],
      },
      {
        label: 'Booking Requests',
        type: 'TEXT' as const,
        value: [`${metric.bookingRequests}`],
      },
      {
        label: 'Accepted Requests',
        type: 'TEXT' as const,
        value: [`${metric.acceptedRequests}`],
      },
      {
        label: 'Scheduled Appointments',
        type: 'TEXT' as const,
        value: [`${metric.scheduledAppointments}`],
      },
      {
        label: 'Revenue',
        type: 'TEXT' as const,
        value: [formatDollarAmount(metric.totalRevenue / 100)],
      },
      includeConversion && {
        label: 'Conversion Rate',
        type: 'TEXT' as const,
        value: [
          metric.totalClicks > 0
            ? `${((metric.bookingRequests / metric.totalClicks) * 100).toFixed(
                1,
              )}%`
            : '-',
        ],
      },
      includeConversion &&
        campaignCost > 0 && {
          label: 'Campaign Cost',
          type: 'TEXT' as const,
          value: [formatDollarAmount(campaignCost / 100)],
        },
      includeConversion &&
        campaignCost > 0 && {
          label: 'ROI',
          type: 'TEXT' as const,
          value: [
            `${((metric.totalRevenue / campaignCost) * 100).toFixed(0)}%`,
          ],
        },
    ].filter(Boolean) as SectionRow[];

  const mediumMetrics = campaignMetrics;
  const mediumKeys = mediumMetrics ? Object.keys(mediumMetrics) : [];
  const hasMetrics = mediumKeys.length > 0;
  const roiTotals: CampaignMetricData | undefined =
    mediumMetrics && hasMetrics
      ? mediumKeys.reduce(
          (acc, key) => ({
            totalClicks: acc.totalClicks + mediumMetrics[key].totalClicks,
            bookingRequests:
              acc.bookingRequests + mediumMetrics[key].bookingRequests,
            acceptedRequests:
              acc.acceptedRequests + mediumMetrics[key].acceptedRequests,
            scheduledAppointments:
              acc.scheduledAppointments +
              mediumMetrics[key].scheduledAppointments,
            totalRevenue: acc.totalRevenue + mediumMetrics[key].totalRevenue,
          }),
          {
            totalClicks: 0,
            bookingRequests: 0,
            acceptedRequests: 0,
            scheduledAppointments: 0,
            totalRevenue: 0,
          },
        )
      : undefined;

  const roiMetricsSection: Section | false = !isDraft &&
    !!roiTotals && {
      name: 'ROI Metrics',
      rows: [
        ...makeMetricRows(roiTotals, true),
        ...(mediumKeys.length > 1
          ? mediumKeys.flatMap(medium => [
              {
                label: '',
                type: 'NODE' as const,
                value: [
                  <Typography key={medium} variant="h5" sx={{ mt: 1 }}>
                    {medium === 'EMAIL' ? 'Email' : medium}
                  </Typography>,
                ],
              },
              ...makeMetricRows(mediumMetrics![medium]),
            ])
          : []),
      ],
    };

  const sections: Section[] = [
    {
      name: 'Overview',
      rows: [
        {
          type: 'NODE',
          label: '',
          value: [
            <Stack gap={0.5}>
              <Typography
                variant="h5"
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {campaign.name}
              </Typography>
              <Typography variant="body2">
                {getCampaignMediumsText(campaign.mediums)}
              </Typography>
              <CampaignBadge campaign={campaign} />
            </Stack>,
          ],
        },
        employeeName && {
          label: 'Created By',
          type: 'TEXT',
          value: [employeeName],
        },
        campaign.sendAt &&
          !isDraft && {
            label: 'Scheduled to Send At',
            type: 'TEXT',
            value: [
              `${format(
                new Date(campaign.sendAt),
                "EEE MMMM d, yyyy 'at' h:mmaaa",
              )} `,
            ],
          },
        editedText && {
          label: 'Last Edited At',
          type: 'TEXT',
          value: [editedText],
        },
      ].filter(Boolean) as SectionRow[],
    },
    !isDraft && {
      name: 'Metrics',
      rows: metrics,
    },
    roiMetricsSection,
    campaign.source.steps.length > 0 && {
      name: 'Content',
      rows: [
        {
          label: '',
          type: 'NODE',
          value: [
            <CampaignPreviewImage key={campaign.id} campaign={campaign} />,
          ],
        },
      ],
    },
  ].filter(Boolean) as Section[];

  return (
    <SideBarModal
      modalOpen={isOpen}
      name="Campaign Details"
      sections={sections}
      setModalOpen={onClose}
      moreActions={disableActions ? undefined : moreActions}
      footerActions={disableActions ? undefined : footerActions}
    />
  );
}

export default CampaignSideBarModal;
